import React, { useState, useEffect, useRef } from "react";
import Navbar from "../nav_bar/Navbar";
import { useNavigate, Link, useLocation } from "react-router-dom";
import {
  requestPasswordResetOtp,
  resetPassword
} from "../../services/userAuthService";
import "./ForgotPasswordPage.css";

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  /* ===============================
     ROUTE STATE
  =============================== */
  const mode = location.state?.mode || "forgot"; // forgot | reset
  const presetEmail = location.state?.email || "";
  const fromProfile = location.state?.fromProfile === true;

  /* ===============================
     STATE
  =============================== */
  const [email, setEmail] = useState(presetEmail);
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);

  // Prevent duplicate OTP calls
  const otpSentRef = useRef(false);

  /* ===============================
     DEBUG LOGS
  =============================== */
  useEffect(() => {
  }, [location.state, mode, presetEmail, fromProfile]);

  /* ===============================
     AUTO SEND OTP (FROM PROFILE)
  =============================== */
  useEffect(() => {
    if (mode === "reset" && fromProfile && email && !otpSentRef.current) {
      otpSentRef.current = true;

      const sendOtpAutomatically = async () => {
        try {
          const res = await requestPasswordResetOtp(email);

          setAlertMessage("OTP sent to your email");
          setShowAlert(true);
          setTimeout(() => setShowAlert(false), 2000);
        } catch (err) {
          setAlertMessage(err.response?.data?.message || "Failed to send OTP");
          setShowAlert(true);
        }
      };

      sendOtpAutomatically();
    }
  }, [mode, fromProfile, email]);

  /* ===============================
     SEND OTP (FROM LOGIN)
  =============================== */
  const handleSendOtp = async () => {
    if (!email) {
      setAlertMessage("Please enter your email");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 2000);
      return;
    }

    setLoading(true);
    try {
      await requestPasswordResetOtp(email);

      navigate("/reset-password", {
        state: { mode: "forgot", email, fromProfile: false }
      });
    } catch (err) {
      setAlertMessage(err.response?.data?.message || "Failed to send OTP");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 2000);
    } finally {
      setLoading(false);
    }
  };

  /* ===============================
     RESET PASSWORD (FROM PROFILE)
  =============================== */
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await resetPassword(email, otp, newPassword);

      setAlertMessage("Password reset successful 🎉");
      setShowAlert(true);

      setTimeout(() => {

        if (fromProfile) {
          // Clear user session
          localStorage.removeItem("token");
          localStorage.removeItem("user");

          // Navigate to login and replace history
          navigate("/login", { replace: true });
        } else {
          navigate("/login");
        }
      }, 2000);
    } catch (err) {
      setAlertMessage(err.response?.data?.message || "Reset failed");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 2000);
    } finally {
      setLoading(false);
    }
  };

  /* ===============================
     RENDER
  =============================== */
  const isProfileFlow = mode === "reset" && fromProfile;
  const isLoginFlow = mode === "forgot" && !fromProfile;

  return (
    <div className="login-page">
      <Navbar />

      <main className="login-wrapper">
        <div className="form-container">
          <h2>{isProfileFlow ? "Reset Password 🔁" : "Forgot Password"}</h2>

          <p className="subtitle">
            {isProfileFlow
              ? "Enter OTP and choose a new password"
              : "Enter your email to receive OTP"}
          </p>

          {isLoginFlow && (
            <>
              <div className="input-group">
                <input
                  type="email"
                  placeholder=" "
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <label>Email address</label>
              </div>

              <button
                className="login-btn"
                onClick={handleSendOtp}
                disabled={loading}
              >
                {loading ? "Sending OTP..." : "Send OTP"}
              </button>

              <div className="bottom-links">
                <Link to="/login">Back to login</Link>
              </div>
            </>
          )}

          {isProfileFlow && (
            <form onSubmit={handleResetPassword}>
              {/* EMAIL */}
              <div className="input-group">
                <input type="email" value={email} disabled />
                <label>Email address</label>
              </div>

              {/* OTP */}
              <div className="input-group">
                <input
                  type="text"
                  required
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder=" "
                />
                <label>OTP</label>
              </div>

              {/* NEW PASSWORD */}
              <div className="input-group">
                <input
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder=" "
                />
                <label>New Password</label>
              </div>

              <button
                className="login-btn"
                type="submit"
                disabled={loading}
              >
                {loading ? "Processing..." : "Reset Password"}
              </button>

              <div className="bottom-links">
                <button
                  className="btn-back-profile"
                  onClick={() => navigate(-1)}
                >
                  ← Back to Profile
                </button>
              </div>
            </form>
          )}
        </div>
      </main>

      {showAlert && <div className="custom-alert">{alertMessage}</div>}
    </div>
  );
};

export default ForgotPasswordPage;
